import { useAssistantContextSnapshot } from 'ai-assistant/context/AssistantContext';
import {
  IChatMessage,
  WELCOME_MESSAGE_CONTENT,
  WelcomeAction,
} from '../models/chat-message';
import { IStreamingCompletionResponse, SimulatedStreamingResponse } from '../models/completion-response';
import { ESETChatService, IChatService, generateChatTitle } from '../services/completion-service';
import { createUUID, UUID } from '../utils/uuid';
import { ASSISTANT_CHAT_INSERT_TAG_EVENT, AssistantChatInsertTagDetail } from '../utils/assistantChatCommands';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dispatchModelChangeAction, requestShowModelInputTab } from 'ai-assistant/utils/modelChangeActions';
import useChatAnimationCoordinator from './useChatAnimationCoordinator';
import type { ChangeEvent } from 'react';
import { FeedbackService, IFeedbackService } from '../services/feedback-service';
import {
  ChatModelAssociation,
  ChatHistoryMessage,
  ChatSessionSummary,
  ChatSessionState,
  ESETHistoryService,
  IChatHistoryService
} from '../services/chat-history-service';
import { useDispatch } from 'react-redux';
import { applyAiModelInputChange } from 'controllers/models/action';
import {
  AssistantChartState,
  AssistantModelSyncState,
  AssistantOutputControlState,
  AssistantSyncPayload,
  buildAssistantSyncChanges,
  createAssistantModelStateFingerprint,
  getAssistantSyncReason,
} from 'ai-assistant/utils/assistantModelSync';
import { summarizeAssistantValue } from 'ai-assistant/utils/summarizeAssistantValue';
import { get as getValueOnPath, isEqual } from 'lodash';

export enum AssistantChatStatus {
  IDLE,
  SUBMITTED,
  STREAMING,
  ERROR,
}

export enum AssistantAIMode {
  ASK = 'ask',
  EDIT = 'edit',
}

export interface AssistantAIModeChangeDetail {
  aiMode: AssistantAIMode;
}

export const ASSISTANT_AI_MODE_EVENT = 'assistant-chat:mode-change';
export const ASSISTANT_AI_MODE_STORAGE_KEY = 'assistant-ai-mode';
const TITLE_RETRY_DELAY_MS = 5000;

export const getStoredAssistantAIMode = (): AssistantAIMode => {
  if (typeof window === 'undefined') {
    return AssistantAIMode.EDIT;
  }

  try {
    const storedMode = window.localStorage.getItem(ASSISTANT_AI_MODE_STORAGE_KEY);
    return storedMode === AssistantAIMode.ASK || storedMode === AssistantAIMode.EDIT
      ? storedMode
      : AssistantAIMode.EDIT;
  } catch (error) {
    console.error('useAssistantChat: failed to read AI mode preference', error);
    return AssistantAIMode.EDIT;
  }
};

export type Feedback = {
  type: 'positive' | 'negative';
  feedbackEntryUUID?: UUID;
  isSubmitted: boolean;
};

export type ToolExecutionStatus = 'started' | 'completed' | 'failed' | 'pending_approval' | 'rejected';

export interface ToolExecution {
  id: string;
  toolName: string;
  status: ToolExecutionStatus;
  arguments?: unknown;
  responsePreview?: string;
  error?: string;
  timestamp: string;
}

export type AssistantMessageBlock =
  | { type: 'message'; id: string; content: string }
  | { type: 'tool'; id: string };

type AssistantChatPersistedState = {
  version: number;
  sessionId: UUID;
  sessionTitle?: string;
  messages: IChatMessage[];
  feedback: Record<UUID, Feedback | null>;
  toolExecutionsByMessage: Record<UUID, ToolExecution[]>;
  assistantMessageBlocksById: Record<UUID, AssistantMessageBlock[]>;
  status: AssistantChatStatus;
  aiMode: AssistantAIMode;
  isOpen: boolean;
  modelAssociation?: ChatModelAssociation | null;
  lastSyncedModelStateFingerprint?: string;
  lastSyncedModelState?: AssistantModelSyncState | null;
};

const CHAT_STATE_VERSION = 4;
const CHAT_STATE_SAVE_DEBOUNCE_MS = 750;

type ToolEventPayload = {
  event?: 'tool_call';
  status: ToolExecutionStatus;
  tool_call_id: string;
  tool_name: string;
  arguments?: unknown;
  response_preview?: string;
  error?: string;
  timestamp?: string;
};

type ModelContextData = {
  currentModel?: {
    modelName?: string;
    modelType?: string;
    draftId?: string;
    sharedRole?: 'owner' | 'viewer';
  } | null;
  currentInput?: unknown;
  status?: string;
  hasStoredChanges?: boolean;
  totalChanges?: number;
  draftId?: string | null;
};

type ModelOutputContextData = {
  model_name?: string | null;
  model_type?: string | null;
  draft_id?: string | null;
  output_text?: string;
};

type ModelOutputChartContextData = {
  name?: string;
  label?: string;
  type?: string;
  narrative?: string;
  digest?: unknown;
  view_state?: unknown;
};

const MODEL_INPUT_CHANGE_TOOL_NAME = 'notify_model_input_changed';

const isModelInputChangeTool = (toolName?: string): boolean => toolName === MODEL_INPUT_CHANGE_TOOL_NAME;

/**
 * Same value-equality check as side-by-side diffs (`collectChangedPaths` in Model.tsx):
 * a proposed edit is real only when the proposed value differs from the current model.
 * Returns true when we cannot evaluate (missing args/input) so review is not hidden.
 */
const isRealPendingModelInputChange = (
  execution: ToolExecution,
  currentInput: unknown,
): boolean => {
  if (!isModelInputChangeTool(execution.toolName) || execution.status !== 'pending_approval') {
    return false;
  }

  if (!execution.arguments || typeof execution.arguments !== 'object' || currentInput == null) {
    return true;
  }

  const args = execution.arguments as Record<string, unknown>;
  const path = (args.input_path || args.inputPath || args.path) as string | undefined;
  if (!path) {
    return true;
  }

  const proposedValue = args.new_value ?? args.newValue ?? args.value;
  const currentValue = getValueOnPath(currentInput as object, path);
  return !isEqual(currentValue, proposedValue);
};

export default function useAssistantChat() {
  const coordinator = useChatAnimationCoordinator();

  const [sessionId, setSessionId] = useState(() => createUUID());
  const [esetSessionId, setEsetSessionId] = useState<UUID | undefined>(undefined);
  const [sessionTitle, setSessionTitle] = useState<string | undefined>(undefined);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [lastTitleAttemptAt, setLastTitleAttemptAt] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);

  const assistantContext = useAssistantContextSnapshot();
  const isModelPage = Boolean(assistantContext['model.current']);
  const modelContextEntry = assistantContext['model.current'];
  const modelContextData = modelContextEntry?.data as ModelContextData | undefined;
  const rawCurrentInput = modelContextEntry?.clientState?.rawCurrentInput ?? modelContextData?.currentInput;
  const isViewOnlyModel = modelContextData?.currentModel?.sharedRole === 'viewer';
  const modelOutputContextData = assistantContext['model.output']?.data as ModelOutputContextData | undefined;
  const modelOutputControlsData = assistantContext['model.output.controls']?.data;
  const hasModelOutput = Boolean(modelOutputContextData?.output_text?.trim());
  const hasModelChanges = Boolean(modelContextData?.hasStoredChanges);
  const modelChangeCount = typeof modelContextData?.totalChanges === 'number'
    ? modelContextData.totalChanges
    : undefined;
  const dispatch = useDispatch();

  const assistantChartStates = useMemo<AssistantChartState[]>(() => {
    return Object.entries(assistantContext)
      .filter(([contextKey]) => contextKey.startsWith('model.output.chart.'))
      .map(([contextKey, entry]) => {
        const data = entry?.data as ModelOutputChartContextData | undefined;
        const fallbackName = contextKey.replace(/^model\.output\.chart\./, '');
        const name = typeof data?.name === 'string' && data.name.trim().length > 0
          ? data.name
          : fallbackName;
        const label = typeof data?.label === 'string' && data.label.trim().length > 0
          ? data.label
          : name;

        return {
          context_key: contextKey,
          name,
          label,
          type: typeof data?.type === 'string' ? data.type : 'unknown',
          narrative: typeof data?.narrative === 'string' ? data.narrative : entry?.summary?.trim() ?? '',
          digest: data?.digest,
          view_state: data?.view_state,
        };
      })
      .sort((left, right) => left.context_key.localeCompare(right.context_key));
  }, [assistantContext]);

  const assistantModelSyncState = useMemo<AssistantModelSyncState | null>(() => {
    if (!modelContextData) {
      return null;
    }

    const currentModel = modelContextData.currentModel;
    const outputControls = Array.isArray(modelOutputControlsData)
      ? modelOutputControlsData
      : [];
    return {
      model_name: currentModel?.modelName ?? null,
      draft_id: currentModel?.draftId ?? modelContextData.draftId ?? modelOutputContextData?.draft_id ?? null,
      model_type: currentModel?.modelType ?? modelOutputContextData?.model_type ?? null,
      status: modelContextData.status ?? null,
      has_stored_changes: Boolean(modelContextData.hasStoredChanges),
      total_changes: typeof modelContextData.totalChanges === 'number' ? modelContextData.totalChanges : 0,
      current_input: summarizeAssistantValue(modelContextData.currentInput ?? null),
      output_text: modelOutputContextData?.output_text?.trim() ?? '',
      output_controls: outputControls as AssistantOutputControlState[],
      charts: assistantChartStates,
    };
  }, [assistantChartStates, modelContextData, modelOutputContextData, modelOutputControlsData]);

  const assistantModelSyncFingerprint = useMemo(() => {
    if (!assistantModelSyncState) {
      return undefined;
    }
    return createAssistantModelStateFingerprint(assistantModelSyncState);
  }, [assistantModelSyncState]);

  const currentAssociationModelName = modelContextData?.currentModel?.modelName?.trim() || null;
  const currentAssociationDraftId = (
    modelContextData?.currentModel?.draftId?.trim()
    || modelContextData?.draftId?.trim()
    || modelOutputContextData?.draft_id?.trim()
    || null
  );
  const currentAssociationModelType = (
    modelContextData?.currentModel?.modelType?.trim()
    || modelOutputContextData?.model_type?.trim()
    || null
  );

  const currentModelAssociation = useMemo<ChatModelAssociation | null>(() => {
    const modelKey = currentAssociationModelName
      ? `saved:${currentAssociationModelName}`
      : currentAssociationDraftId
        ? `draft:${currentAssociationDraftId}`
        : null;

    if (!modelKey && !currentAssociationModelType) {
      return null;
    }

    return {
      modelName: currentAssociationModelName,
      draftId: currentAssociationDraftId,
      modelType: currentAssociationModelType,
      modelKey,
    };
  }, [currentAssociationDraftId, currentAssociationModelName, currentAssociationModelType]);

  const handleEsetSessionSync = useCallback((nextSessionId: UUID) => {
    setEsetSessionId(prevSessionId => {
      if (prevSessionId && prevSessionId !== nextSessionId) {
        setSessionTitle(undefined);
        setLastTitleAttemptAt(0);
      }
      return nextSessionId;
    });
    setSessionId(nextSessionId);
    if (currentModelAssociation) {
      setSessionModelAssociation(currentModelAssociation);
    }
  }, [currentModelAssociation, setSessionId, setSessionTitle, setLastTitleAttemptAt]);

  const chatService = useMemo<IChatService>(() => {
    return new ESETChatService(esetSessionId, handleEsetSessionSync);
  }, [esetSessionId, handleEsetSessionSync]);

  const historyService = useMemo<IChatHistoryService>(() => new ESETHistoryService(), []);
  const feedbackService = useMemo<IFeedbackService>(() => new FeedbackService(), []);

  const [messages, setMessages] = useState<IChatMessage[]>(getInitialMessages());
  const [feedback, setFeedback] = useState<Record<UUID, Feedback | null>>({});
  const [toolExecutionsByMessage, setToolExecutionsByMessage] = useState<Record<UUID, ToolExecution[]>>({});
  const [assistantMessageBlocksById, setAssistantMessageBlocksById] = useState<Record<UUID, AssistantMessageBlock[]>>({});

  const [input, setInput] = useState<string>('');
  const [status, setStatus] = useState<AssistantChatStatus>(AssistantChatStatus.IDLE);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [aiMode, setAIModeState] = useState<AssistantAIMode>(getStoredAssistantAIMode);
  const effectiveAIMode = isViewOnlyModel ? AssistantAIMode.ASK : aiMode;
  const [lastSyncedModelStateFingerprint, setLastSyncedModelStateFingerprint] = useState<string | undefined>(undefined);
  const [lastSyncedModelState, setLastSyncedModelState] = useState<AssistantModelSyncState | null>(null);
  const [sessionModelAssociation, setSessionModelAssociation] = useState<ChatModelAssociation | null>(null);
  const sessionStateSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSessionStateRef = useRef<string | null>(null);
  const lastAutoRestoredModelKeyRef = useRef<string | null>(null);
  const autoRestoreStateRef = useRef({
    sessionId,
    esetSessionId,
    input,
    status,
    messageCount: messages.length,
  });

  const setAIMode = useCallback((nextMode: AssistantAIMode) => {
    if (isViewOnlyModel && nextMode !== AssistantAIMode.ASK) {
      return;
    }
    setAIModeState(prev => (prev === nextMode ? prev : nextMode));
  }, [isViewOnlyModel]);

  useEffect(() => {
    lastSavedSessionStateRef.current = null;
  }, [esetSessionId]);

  useEffect(() => {
    autoRestoreStateRef.current = {
      sessionId,
      esetSessionId,
      input,
      status,
      messageCount: messages.length,
    };
  }, [esetSessionId, input, messages.length, sessionId, status]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(ASSISTANT_AI_MODE_STORAGE_KEY, aiMode);
    } catch (error) {
      console.error('useAssistantChat: failed to persist AI mode preference', error);
    }
  }, [aiMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const detail: AssistantAIModeChangeDetail = { aiMode: effectiveAIMode };
    window.dispatchEvent(new CustomEvent<AssistantAIModeChangeDetail>(ASSISTANT_AI_MODE_EVENT, { detail }));
  }, [effectiveAIMode]);

  const registerToolEvent = useCallback((messageId: UUID, payload: ToolEventPayload) => {
    if (!payload || (payload.event && payload.event !== 'tool_call')) {
      return;
    }

    setToolExecutionsByMessage(prev => {
      const existing = prev[messageId] ?? [];
      const targetIndex = existing.findIndex(event => event.id === payload.tool_call_id);
      const timestamp = payload.timestamp ?? new Date().toISOString();

      const baseEvent: ToolExecution = targetIndex >= 0
        ? existing[targetIndex]
        : {
          id: payload.tool_call_id,
          toolName: payload.tool_name ?? 'unknown',
          status: payload.status,
          arguments: payload.arguments,
          responsePreview: payload.response_preview,
          error: payload.error,
          timestamp,
        };

      const updatedEvent: ToolExecution = {
        ...baseEvent,
        status: payload.status ?? baseEvent.status,
        toolName: payload.tool_name ?? baseEvent.toolName,
        timestamp,
      };

      if (payload.arguments !== undefined) {
        updatedEvent.arguments = payload.arguments;
      }
      if (payload.response_preview !== undefined) {
        updatedEvent.responsePreview = payload.response_preview;
      }
      if (payload.error !== undefined) {
        updatedEvent.error = payload.error;
      }

      if (isModelInputChangeTool(updatedEvent.toolName)) {
        if (payload.status === 'started') {
          updatedEvent.status = 'pending_approval';
        } else if (payload.status === 'completed' && baseEvent.status === 'pending_approval') {
          updatedEvent.status = 'pending_approval';
        } else if (baseEvent.status === 'rejected') {
          updatedEvent.status = 'rejected';
        }
      }

      const nextEvents = [...existing];
      if (targetIndex >= 0) {
        nextEvents[targetIndex] = updatedEvent;
      } else {
        nextEvents.push(updatedEvent);
      }

      return {
        ...prev,
        [messageId]: nextEvents,
      };
    });
  }, []);

  const registerToolBlock = useCallback((messageId: UUID, toolCallId?: string) => {
    if (!toolCallId) {
      return;
    }

    setAssistantMessageBlocksById(prev => {
      const existing = prev[messageId] ?? [];
      if (existing.some(block => block.type === 'tool' && block.id === toolCallId)) {
        return prev;
      }

      return {
        ...prev,
        [messageId]: [...existing, { type: 'tool', id: toolCallId }],
      };
    });
  }, []);

  const appendAssistantBlockContent = useCallback((messageId: UUID, text: string) => {
    if (!text) {
      return;
    }

    setAssistantMessageBlocksById(prev => {
      const existing = prev[messageId] ?? [];
      const lastBlock = existing[existing.length - 1];

      if (lastBlock && lastBlock.type === 'message') {
        const next = [...existing];
        next[next.length - 1] = {
          ...lastBlock,
          content: `${lastBlock.content}${text}`,
        };
        return {
          ...prev,
          [messageId]: next,
        };
      }

      return {
        ...prev,
        [messageId]: [...existing, { type: 'message', id: createUUID(), content: text }],
      };
    });
  }, []);

  const extractAiInputChangeArgs = useCallback((candidate: ToolEventPayload | ToolExecution | null | undefined): Record<string, unknown> | null => {
    const toolName = candidate ? ('tool_name' in candidate ? candidate.tool_name : candidate.toolName) : undefined;
    if (!isModelInputChangeTool(toolName)) {
      return null;
    }

    if (!candidate?.arguments || typeof candidate.arguments !== 'object') {
      return null;
    }

    return candidate.arguments as Record<string, unknown>;
  }, []);

  const applyAiInputChange = useCallback((args: Record<string, unknown> | null) => {
    if (!args) {
      return;
    }

    const path = (args.input_path || args.inputPath || args.path) as string | undefined;
    if (!path) {
      return;
    }

    const value = args.new_value ?? args.newValue ?? args.value;
    const modelName = (args.model_name || args.modelName) as string | undefined;
    const draftId = (args.draft_id || args.draftId) as string | undefined;
    const modelType = (args.model_type || args.modelType) as string | undefined;

    dispatch(applyAiModelInputChange({ path, value, modelName, draftId, modelType }));
  }, [dispatch]);

  const updateToolExecutionStatus = useCallback((toolCallId: string, updater: (execution: ToolExecution) => ToolExecution) => {
    setToolExecutionsByMessage(prev => {
      let didChange = false;
      const nextEntries = Object.entries(prev).map(([messageId, executions]) => {
        const nextExecutions = executions.map(execution => {
          if (execution.id !== toolCallId) {
            return execution;
          }

          didChange = true;
          return updater(execution);
        });

        return [messageId, nextExecutions] as const;
      });

      if (!didChange) {
        return prev;
      }

      return Object.fromEntries(nextEntries) as Record<UUID, ToolExecution[]>;
    });
  }, []);

  const collectPendingToolExecutions = useCallback((): ToolExecution[] => {
    return Object.values(toolExecutionsByMessage)
      .flat()
      .filter(execution => (
        execution.status === 'pending_approval'
        && isModelInputChangeTool(execution.toolName)
      ));
  }, [toolExecutionsByMessage]);

  const approvePendingToolChanges = useCallback(() => {
    let appliedAny = false;
    collectPendingToolExecutions().forEach(execution => {
      // Skip applying no-ops (same check as side-by-side: proposed value === current).
      if (isRealPendingModelInputChange(execution, rawCurrentInput)) {
        applyAiInputChange(extractAiInputChangeArgs(execution));
        appliedAny = true;
      }
      updateToolExecutionStatus(execution.id, current => ({
        ...current,
        status: 'completed',
        error: undefined,
      }));
    });
    // Ensure the model Input tab is visible so applied highlights are not hidden behind Output.
    if (appliedAny) {
      requestShowModelInputTab();
    }
  }, [
    applyAiInputChange,
    collectPendingToolExecutions,
    extractAiInputChangeArgs,
    rawCurrentInput,
    updateToolExecutionStatus,
  ]);

  const rejectPendingToolChanges = useCallback(() => {
    collectPendingToolExecutions().forEach(execution => {
      updateToolExecutionStatus(execution.id, current => ({
        ...current,
        status: 'rejected',
        error: 'Change not applied. User rejected the proposed edit.',
      }));
    });
  }, [collectPendingToolExecutions, updateToolExecutionStatus]);

  useEffect(() => {
    const handleInsertTag = (event: Event) => {
      const customEvent = event as CustomEvent<AssistantChatInsertTagDetail>;
      const detail = customEvent.detail;
      if (!detail?.tag) {
        return;
      }

      if (detail.openAssistant !== false) {
        setIsOpen(true);
      }

      setInput(prevInput => {
        if (detail.append === false) {
          return detail.tag;
        }

        const current = prevInput ?? '';
        if (current.trim().length === 0) {
          return detail.tag;
        }

        const separator = detail.separator ?? ' ';
        const trimmed = current.replace(/\s+$/, '');
        return `${trimmed}${separator}${detail.tag}`;
      });
    };

    const listener: EventListener = handleInsertTag;
    window.addEventListener(ASSISTANT_CHAT_INSERT_TAG_EVENT, listener);

    return () => {
      window.removeEventListener(ASSISTANT_CHAT_INSERT_TAG_EVENT, listener);
    };
  }, [setIsOpen, setInput]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const setInputValue = useCallback((value: string) => {
    setInput(value);
  }, []);

  const handleUndoAllChanges = useCallback(() => {
    dispatchModelChangeAction('undo-all');
  }, []);

  const handleKeepAllChanges = useCallback(() => {
    dispatchModelChangeAction('keep-all');
  }, []);

  const stringifyPayload = (payload: unknown): string => {
    if (payload === undefined || payload === null) {
      return '';
    }

    if (typeof payload === 'string') {
      return payload;
    }

    try {
      return JSON.stringify(payload, null, 2);
    } catch (error) {
      console.error('useAssistantChat: unable to stringify payload', error);
      return String(payload);
    }
  };

  const buildHistoryMessages = useCallback((history: ChatHistoryMessage[], baseTimestamp?: number): IChatMessage[] => {
    const startTimestamp = Number.isFinite(baseTimestamp) ? Number(baseTimestamp) : Date.now();
    return history.map((message, index) => ({
      id: createUUID(),
      createdAt: new Date(startTimestamp + index).toISOString(),
      type: 'text',
      role: message.role,
      content: message.content ?? '',
    }));
  }, []);

  const buildPersistedState = useCallback((): AssistantChatPersistedState => ({
    version: CHAT_STATE_VERSION,
    sessionId: (esetSessionId ?? sessionId),
    sessionTitle,
    messages,
    feedback,
    toolExecutionsByMessage,
    assistantMessageBlocksById,
    status,
    aiMode,
    isOpen,
    modelAssociation: sessionModelAssociation ?? currentModelAssociation,
    lastSyncedModelStateFingerprint,
    lastSyncedModelState,
  }), [
    assistantMessageBlocksById,
    aiMode,
    esetSessionId,
    feedback,
    isOpen,
    currentModelAssociation,
    sessionModelAssociation,
    lastSyncedModelState,
    lastSyncedModelStateFingerprint,
    messages,
    sessionId,
    sessionTitle,
    status,
    toolExecutionsByMessage,
  ]);

  const normalizeRecord = <T,>(value: unknown): Record<UUID, T> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }
    return value as Record<UUID, T>;
  };

  const normalizeAIMode = (value: unknown): AssistantAIMode => {
    return value === AssistantAIMode.ASK || value === AssistantAIMode.EDIT
      ? value
      : getStoredAssistantAIMode();
  };

  const normalizeStatus = (value: unknown): AssistantChatStatus => {
    const allowed = [
      AssistantChatStatus.IDLE,
      AssistantChatStatus.SUBMITTED,
      AssistantChatStatus.STREAMING,
      AssistantChatStatus.ERROR,
    ];
    return allowed.includes(value as AssistantChatStatus) ? (value as AssistantChatStatus) : AssistantChatStatus.IDLE;
  };

  const normalizeAssistantModelSyncState = (value: unknown): AssistantModelSyncState | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const candidate = value as Partial<AssistantModelSyncState>;
    return {
      model_name: typeof candidate.model_name === 'string' ? candidate.model_name : null,
      draft_id: typeof candidate.draft_id === 'string' ? candidate.draft_id : null,
      model_type: typeof candidate.model_type === 'string' ? candidate.model_type : null,
      status: typeof candidate.status === 'string' ? candidate.status : null,
      has_stored_changes: Boolean(candidate.has_stored_changes),
      total_changes: typeof candidate.total_changes === 'number' ? candidate.total_changes : 0,
      current_input: summarizeAssistantValue(candidate.current_input ?? null),
      output_text: typeof candidate.output_text === 'string' ? candidate.output_text : '',
      output_controls: Array.isArray(candidate.output_controls)
        ? (candidate.output_controls as AssistantOutputControlState[])
        : [],
      charts: Array.isArray(candidate.charts)
        ? (candidate.charts as AssistantChartState[])
        : [],
    };
  };

  const normalizeChatMessage = (raw: unknown): IChatMessage | null => {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const candidate = raw as {
      id?: string;
      createdAt?: string;
      role?: string;
      content?: string;
      type?: string;
      isWelcome?: boolean;
      examplePrompts?: unknown;
    };
    if (candidate.role !== 'user' && candidate.role !== 'assistant' && candidate.role !== 'system') {
      return null;
    }

    // Legacy flow welcome messages stored examplePrompts and type "flow" on the first assistant bubble.
    const isLegacyWelcome =
      candidate.role === 'assistant'
      && (
        candidate.isWelcome === true
        || (candidate.type === 'flow' && Array.isArray(candidate.examplePrompts))
      );

    return {
      id: typeof candidate.id === 'string' ? (candidate.id as UUID) : createUUID(),
      createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : new Date().toISOString(),
      role: candidate.role,
      content: typeof candidate.content === 'string' ? candidate.content : '',
      type: 'text',
      isWelcome: isLegacyWelcome,
    };
  };

  const normalizePersistedState = useCallback((raw: ChatSessionState | null): AssistantChatPersistedState | null => {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const candidate = raw as Partial<AssistantChatPersistedState>;
    const normalizedMessages = Array.isArray(candidate.messages)
      ? candidate.messages
        .map(normalizeChatMessage)
        .filter((message): message is IChatMessage => message !== null)
      : [];

    return {
      version: typeof candidate.version === 'number' ? candidate.version : CHAT_STATE_VERSION,
      sessionId: typeof candidate.sessionId === 'string' ? (candidate.sessionId as UUID) : (esetSessionId ?? sessionId),
      sessionTitle: typeof candidate.sessionTitle === 'string' ? candidate.sessionTitle : undefined,
      messages: normalizedMessages,
      feedback: normalizeRecord<Feedback | null>(candidate.feedback),
      toolExecutionsByMessage: normalizeRecord<ToolExecution[]>(candidate.toolExecutionsByMessage),
      assistantMessageBlocksById: normalizeRecord<AssistantMessageBlock[]>(candidate.assistantMessageBlocksById),
      status: normalizeStatus(candidate.status),
      aiMode: normalizeAIMode(candidate.aiMode),
      isOpen: typeof candidate.isOpen === 'boolean' ? candidate.isOpen : true,
      modelAssociation: candidate.modelAssociation && typeof candidate.modelAssociation === 'object'
        ? (candidate.modelAssociation as ChatModelAssociation)
        : null,
      lastSyncedModelStateFingerprint: typeof candidate.lastSyncedModelStateFingerprint === 'string'
        ? candidate.lastSyncedModelStateFingerprint
        : undefined,
      lastSyncedModelState: normalizeAssistantModelSyncState(candidate.lastSyncedModelState),
    };
  }, [esetSessionId, sessionId]);

  const loadSessionFromHistory = useCallback(async (targetSessionId: UUID, options?: { timestamp?: number; sessionName?: string; modelAssociation?: ChatModelAssociation | null }) => {
    setIsRestoringSession(true);
    setStatus(AssistantChatStatus.SUBMITTED);
    setInput('');
    setToolExecutionsByMessage({});
    setAssistantMessageBlocksById({});
    setFeedback({});

    try {
      const storedState = await historyService.fetchSessionState(targetSessionId);
      const normalizedState = normalizePersistedState(storedState);

      if (!normalizedState) {
        const history = await historyService.fetchChatHistory(targetSessionId);
        const baseTimestamp = options?.timestamp ? options.timestamp * 1000 : Date.now();
        const historyMessages = buildHistoryMessages(history, baseTimestamp);
        const nextMessages = historyMessages.length > 0
          ? [createSystemMessage(), ...historyMessages]
          : getInitialMessages();

        setMessages(nextMessages);
        setSessionId(targetSessionId);
        setEsetSessionId(targetSessionId);
        setSessionTitle(options?.sessionName);
        setAIModeState(getStoredAssistantAIMode());
        setLastTitleAttemptAt(0);
        setIsGeneratingTitle(false);
        setStatus(AssistantChatStatus.IDLE);
        setIsOpen(true);
        setLastSyncedModelStateFingerprint(undefined);
        setLastSyncedModelState(null);
        setSessionModelAssociation(options?.modelAssociation ?? null);
        lastSavedSessionStateRef.current = null;
        return;
      }

      const resolvedState = {
        ...normalizedState,
        sessionId: targetSessionId,
      };

      const nextMessages = resolvedState.messages.length > 0
        ? resolvedState.messages
        : getInitialMessages();

      setMessages(nextMessages);
      setFeedback(resolvedState.feedback);
      setToolExecutionsByMessage(resolvedState.toolExecutionsByMessage);
      setAssistantMessageBlocksById(resolvedState.assistantMessageBlocksById);
      setInput('');
      setStatus(resolvedState.status);
      setAIModeState(resolvedState.aiMode);
      setSessionId(targetSessionId);
      setEsetSessionId(targetSessionId);
      setSessionTitle(resolvedState.sessionTitle ?? options?.sessionName);
      setLastTitleAttemptAt(0);
      setIsGeneratingTitle(false);
      setIsOpen(true);
      const restoredSyncState = resolvedState.lastSyncedModelState ?? null;
      setLastSyncedModelState(restoredSyncState);
      setLastSyncedModelStateFingerprint(
        restoredSyncState
          ? createAssistantModelStateFingerprint(restoredSyncState)
          : resolvedState.lastSyncedModelStateFingerprint,
      );
      setSessionModelAssociation(resolvedState.modelAssociation ?? options?.modelAssociation ?? null);

      try {
        lastSavedSessionStateRef.current = JSON.stringify(resolvedState);
      } catch (error) {
        console.error('useAssistantChat: failed to cache restored state', error);
        lastSavedSessionStateRef.current = null;
      }
    } catch (error) {
      console.error('useAssistantChat: failed to restore session', error);
      setStatus(AssistantChatStatus.ERROR);
      throw error;
    } finally {
      setIsRestoringSession(false);
    }
  }, [buildHistoryMessages, historyService, normalizePersistedState]);

  const getAssociationModelKey = useCallback((association: ChatModelAssociation | null | undefined): string | null => {
    const storedModelKey = association?.modelKey?.trim();
    if (storedModelKey) {
      return storedModelKey;
    }

    const modelName = association?.modelName?.trim();
    if (modelName) {
      return `saved:${modelName}`;
    }

    const draftId = association?.draftId?.trim();
    if (draftId) {
      return `draft:${draftId}`;
    }

    return null;
  }, []);

  const getSessionModelAssociation = useCallback((session: ChatSessionSummary): ChatModelAssociation => ({
    modelName: session.modelName,
    draftId: session.draftId,
    modelType: session.modelType ?? session.modelTypes?.[0] ?? null,
    modelKey: getAssociationModelKey(session),
  }), [getAssociationModelKey]);

  const modelAssociationsMatch = useCallback((
    left: ChatModelAssociation | null | undefined,
    right: ChatModelAssociation | null | undefined,
  ): boolean => {
    const leftModelKey = getAssociationModelKey(left);
    const rightModelKey = getAssociationModelKey(right);
    if (leftModelKey && rightModelKey) {
      return leftModelKey === rightModelKey;
    }

    const leftModelName = left?.modelName?.trim();
    const rightModelName = right?.modelName?.trim();
    if (leftModelName && rightModelName) {
      return leftModelName === rightModelName;
    }

    const leftDraftId = left?.draftId?.trim();
    const rightDraftId = right?.draftId?.trim();
    return Boolean(leftDraftId && rightDraftId && leftDraftId === rightDraftId);
  }, [getAssociationModelKey]);

  const getPersistedModelAssociation = useCallback((state: ChatSessionState | null): ChatModelAssociation | null => {
    if (!state || typeof state !== 'object' || Array.isArray(state)) {
      return null;
    }

    const modelAssociation = (state as Partial<AssistantChatPersistedState>).modelAssociation;
    if (!modelAssociation || typeof modelAssociation !== 'object' || Array.isArray(modelAssociation)) {
      return null;
    }

    return modelAssociation as ChatModelAssociation;
  }, []);

  const findLatestMatchingModelSession = useCallback(async (
    sessions: ChatSessionSummary[],
    modelAssociation: ChatModelAssociation,
  ): Promise<{ session: ChatSessionSummary; modelAssociation: ChatModelAssociation } | null> => {
    const sortedSessions = [...sessions].sort((left, right) => right.timestamp - left.timestamp);
    const summaryMatch = sortedSessions.find(session =>
      modelAssociationsMatch(getSessionModelAssociation(session), modelAssociation)
    );

    if (summaryMatch) {
      return {
        session: summaryMatch,
        modelAssociation: getSessionModelAssociation(summaryMatch),
      };
    }

    for (const session of sortedSessions) {
      if (getAssociationModelKey(getSessionModelAssociation(session))) {
        continue;
      }

      const persistedState = await historyService.fetchSessionState(session.uuid).catch(() => null);
      const persistedAssociation = getPersistedModelAssociation(persistedState);
      if (modelAssociationsMatch(persistedAssociation, modelAssociation)) {
        return {
          session,
          modelAssociation: persistedAssociation as ChatModelAssociation,
        };
      }
    }

    return null;
  }, [
    getAssociationModelKey,
    getPersistedModelAssociation,
    getSessionModelAssociation,
    historyService,
    modelAssociationsMatch,
  ]);

  useEffect(() => {
    const modelKey = getAssociationModelKey(currentModelAssociation);

    if (!isModelPage || !modelKey || !currentModelAssociation) {
      lastAutoRestoredModelKeyRef.current = null;
      return;
    }

    if (lastAutoRestoredModelKeyRef.current === modelKey) {
      return;
    }

    let isCancelled = false;
    let didCompleteAttempt = false;
    const activeModelAssociation = currentModelAssociation;
    const initialRestoreState = autoRestoreStateRef.current;
    lastAutoRestoredModelKeyRef.current = modelKey;

    const restoreLatestModelSession = async () => {
      try {
        const sessions = await historyService.fetchSessions(activeModelAssociation);

        if (isCancelled) {
          return;
        }

        const latestModelMatch = await findLatestMatchingModelSession(sessions, activeModelAssociation);

        if (isCancelled || !latestModelMatch) {
          if (!isCancelled) {
            didCompleteAttempt = true;
          }
          return;
        }

        const { session: latestModelSession, modelAssociation } = latestModelMatch;
        if (latestModelSession.uuid === esetSessionId || latestModelSession.uuid === sessionId) {
          didCompleteAttempt = true;
          return;
        }

        const currentRestoreState = autoRestoreStateRef.current;
        const userChangedChatDuringRestore = (
          currentRestoreState.sessionId !== initialRestoreState.sessionId
          || currentRestoreState.esetSessionId !== initialRestoreState.esetSessionId
          || currentRestoreState.input !== initialRestoreState.input
          || currentRestoreState.status !== initialRestoreState.status
          || currentRestoreState.messageCount !== initialRestoreState.messageCount
        );

        if (userChangedChatDuringRestore) {
          didCompleteAttempt = true;
          return;
        }

        await loadSessionFromHistory(latestModelSession.uuid, {
          timestamp: latestModelSession.timestamp,
          sessionName: latestModelSession.sessionName,
          modelAssociation,
        });
        didCompleteAttempt = true;
      } catch (error) {
        if (!isCancelled) {
          lastAutoRestoredModelKeyRef.current = null;
          console.error('useAssistantChat: failed to auto-restore model session', error);
        }
      }
    };

    void restoreLatestModelSession();

    return () => {
      isCancelled = true;
      if (!didCompleteAttempt && lastAutoRestoredModelKeyRef.current === modelKey) {
        lastAutoRestoredModelKeyRef.current = null;
      }
    };
  }, [
    currentModelAssociation,
    esetSessionId,
    findLatestMatchingModelSession,
    getAssociationModelKey,
    historyService,
    isModelPage,
    loadSessionFromHistory,
    sessionId,
  ]);

  const persistSessionState = useCallback(async (state: AssistantChatPersistedState): Promise<boolean> => {
    if (!esetSessionId) {
      return false;
    }

    try {
      await historyService.saveSessionState(esetSessionId, state as ChatSessionState);
      return true;
    } catch (error) {
      console.error('useAssistantChat: failed to persist session state', error);
      return false;
    }
  }, [esetSessionId, historyService]);

  const scheduleSessionStateSave = useCallback(() => {
    if (!esetSessionId) {
      return;
    }

    if (sessionStateSaveTimeoutRef.current) {
      clearTimeout(sessionStateSaveTimeoutRef.current);
      sessionStateSaveTimeoutRef.current = null;
    }

    if (isRestoringSession) {
      return;
    }

    if (status === AssistantChatStatus.SUBMITTED || status === AssistantChatStatus.STREAMING) {
      return;
    }

    const state = buildPersistedState();
    let serializedState: string;
    try {
      serializedState = JSON.stringify(state);
    } catch (error) {
      console.error('useAssistantChat: failed to serialize session state', error);
      return;
    }

    if (serializedState === lastSavedSessionStateRef.current) {
      return;
    }

    sessionStateSaveTimeoutRef.current = setTimeout(() => {
      persistSessionState(state).then(success => {
        if (success) {
          lastSavedSessionStateRef.current = serializedState;
        }
        sessionStateSaveTimeoutRef.current = null;
      });
    }, CHAT_STATE_SAVE_DEBOUNCE_MS);
  }, [
    buildPersistedState,
    esetSessionId,
    isRestoringSession,
    persistSessionState,
    status,
  ]);

  useEffect(() => {
    scheduleSessionStateSave();

    return () => {
      if (sessionStateSaveTimeoutRef.current) {
        clearTimeout(sessionStateSaveTimeoutRef.current);
        sessionStateSaveTimeoutRef.current = null;
      }
    };
  }, [scheduleSessionStateSave]);

  const buildMessagesWithToolSummaries = useCallback((baseMessages: IChatMessage[]): IChatMessage[] => {
    const augmentedMessages: IChatMessage[] = [];

    baseMessages.forEach(message => {
      augmentedMessages.push(message);

      if (message.role !== 'assistant') {
        return;
      }

      const executions = toolExecutionsByMessage[message.id];
      if (!executions?.length) {
        return;
      }

      const summaryLines = executions.map((execution, index) => {
        const segments = [
          `tool: ${execution.toolName}`,
          `status: ${execution.status}`,
        ];

        if (execution.arguments !== undefined) {
          segments.push(`input:\n${stringifyPayload(execution.arguments)}`);
        }

        if (execution.responsePreview) {
          segments.push(`output:\n${stringifyPayload(execution.responsePreview)}`);
        }

        if (execution.error) {
          segments.push(`error:\n${execution.error}`);
        }

        return `${index + 1}. ${segments.join('\n')}`;
      });

      augmentedMessages.push({
        id: `${message.id}-tool-log`,
        createdAt: message.createdAt,
        type: 'text',
        role: 'assistant',
        content: `Tool call summary:\n${summaryLines.join('\n\n')}`,
      });
    });

    return augmentedMessages;
  }, [toolExecutionsByMessage]);

  const hasConversationHistoryForSync = useMemo(() => {
    return messages.some(message =>
      message.role === 'user' ||
      (message.role === 'assistant' && !message.isWelcome && message.content.trim().length > 0)
    );
  }, [messages]);

  const pendingToolApprovalCount = useMemo(() => {
    return Object.values(toolExecutionsByMessage)
      .flat()
      .filter(execution => isRealPendingModelInputChange(execution, rawCurrentInput))
      .length;
  }, [rawCurrentInput, toolExecutionsByMessage]);

  const hasPendingToolApprovals = pendingToolApprovalCount > 0;

  const buildAssistantSyncPayload = useCallback((): AssistantSyncPayload | undefined => {
    if (!assistantModelSyncState || !assistantModelSyncFingerprint) {
      return undefined;
    }

    const hasBaseline = Boolean(lastSyncedModelStateFingerprint && lastSyncedModelState);
    const shouldForceResumeSync = !hasBaseline && hasConversationHistoryForSync;
    const fingerprintChanged = hasBaseline && lastSyncedModelStateFingerprint !== assistantModelSyncFingerprint;

    if (!shouldForceResumeSync && !fingerprintChanged) {
      return undefined;
    }

    const changes = lastSyncedModelState
      ? buildAssistantSyncChanges(lastSyncedModelState, assistantModelSyncState)
      : {
        inputs_changed: [],
        output_controls_changed: assistantModelSyncState.output_controls.map(control => control.path),
        output_sections_changed: [],
        charts_changed: assistantModelSyncState.charts.map(chart => chart.label || chart.name || chart.context_key),
        summary_changed: assistantModelSyncState.output_text.length > 0,
      };

    return {
      previous_fingerprint: lastSyncedModelStateFingerprint,
      current_fingerprint: assistantModelSyncFingerprint,
      reason: getAssistantSyncReason(changes, shouldForceResumeSync ? undefined : lastSyncedModelState ?? undefined),
      current_state: assistantModelSyncState,
      changes,
    };
  }, [
    assistantModelSyncFingerprint,
    assistantModelSyncState,
    hasConversationHistoryForSync,
    lastSyncedModelState,
    lastSyncedModelStateFingerprint,
  ]);

  useEffect(() => {
    const activeSessionId = esetSessionId;
    if (!activeSessionId || sessionTitle) {
      return;
    }

    const now = Date.now();
    if (isGeneratingTitle || (lastTitleAttemptAt && now - lastTitleAttemptAt < TITLE_RETRY_DELAY_MS)) {
      return;
    }

    const hasAssistantReply = messages.some(message => message.role === 'assistant' && message.content.trim().length > 0);
    const hasUserMessage = messages.some(message => message.role === 'user' && message.content.trim().length > 0);

    if (!hasAssistantReply || !hasUserMessage) {
      return;
    }

    const messagesForTitle = messages
      .filter(message => message.role === 'user' || message.role === 'assistant')
      .slice(0, 6);

    setIsGeneratingTitle(true);
    setLastTitleAttemptAt(now);

    generateChatTitle(activeSessionId, messagesForTitle)
      .then(title => setSessionTitle(title))
      .catch(error => {
        console.error('useAssistantChat: failed to generate chat title', error);
      })
      .finally(() => setIsGeneratingTitle(false));
  }, [esetSessionId, isGeneratingTitle, lastTitleAttemptAt, messages, sessionTitle]);

  const streamCompletionResponse = useCallback((completionResponse: IStreamingCompletionResponse, updatingChatId: UUID) => {
    setStatus(AssistantChatStatus.STREAMING);

    let accumulatedAssistantContent = '';

    const appendAssistantContent = (text: string) => {
      if (!text) {
        return;
      }
      accumulatedAssistantContent += text;
      appendAssistantBlockContent(updatingChatId, text);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === updatingChatId ? { ...msg, content: msg.content + text } : msg
        )
      );
    };

    const handleToolEvent = (payloadText: string) => {
      try {
        const payload: ToolEventPayload = JSON.parse(payloadText);
        registerToolEvent(updatingChatId, payload);
        registerToolBlock(updatingChatId, payload.tool_call_id);
      } catch (error) {
        console.error('useAssistantChat: failed to parse tool event payload', error, payloadText);
      }
    };

    completionResponse.onEvent(({ event, data }) => {
      if (!data) {
        return;
      }
      if (event === 'tool_call') {
        handleToolEvent(data);
        return;
      }
      if (event === 'message' || event === 'error') {
        appendAssistantContent(data);
      }
    }).then(() => {
      setStatus(AssistantChatStatus.IDLE);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === updatingChatId ? { ...msg, content: accumulatedAssistantContent } : msg
        )
      );
    }).catch((error: unknown) => {
      console.error('useAssistantChat: streamCompletionResponse', error);
      setStatus(AssistantChatStatus.ERROR);
    });
  }, [appendAssistantBlockContent, registerToolBlock, registerToolEvent]);

  const submitUserMessage = useCallback(async (content: string, nextMessageId: UUID) => {
    const currentInput = content;
    const assistantSync = buildAssistantSyncPayload();

    if (status !== AssistantChatStatus.IDLE && status !== AssistantChatStatus.ERROR) {
      return false;
    }

    if (hasPendingToolApprovals) {
      return false;
    }

    if (currentInput.trim() === '') {
      return false;
    }

    setInput('');

    coordinator.withNextRender(() => {
      setInput('');
    });

    const userMessage: IChatMessage = {
      id: nextMessageId,
      createdAt: new Date().toISOString(),
      type: 'text',
      role: 'user',
      content: currentInput
    };

    const baseMessagesForApi = [...messages, userMessage];
    const messagesForApi = buildMessagesWithToolSummaries(baseMessagesForApi);

    if (currentModelAssociation) {
      setSessionModelAssociation(currentModelAssociation);
    }

    setMessages(prev => [...prev, userMessage]);
    setStatus(AssistantChatStatus.SUBMITTED);

    await coordinator.waitForUserMessageAnimation();

    const assistantNewMessage: IChatMessage = {
      id: createUUID(),
      createdAt: new Date().toISOString(),
      type: 'text',
      role: 'assistant',
      content: ''
    };

    setMessages(prev => [...prev, assistantNewMessage]);
    setToolExecutionsByMessage(prev => ({
      ...prev,
      [assistantNewMessage.id]: []
    }));
    setAssistantMessageBlocksById(prev => ({
      ...prev,
      [assistantNewMessage.id]: [],
    }));

    try {
      const response = await chatService.sendMessage(messagesForApi, assistantContext, {
        aiMode: effectiveAIMode,
        assistantSync,
      });
      if (assistantModelSyncState && assistantModelSyncFingerprint) {
        setLastSyncedModelStateFingerprint(assistantModelSyncFingerprint);
        setLastSyncedModelState(assistantModelSyncState);
      }
      streamCompletionResponse(response, assistantNewMessage.id);
    } catch (apiError) {
      console.error('Completion API error:', apiError);
      setMessages(prev => prev.filter(msg => msg.id !== assistantNewMessage.id));
      setToolExecutionsByMessage(prev => {
        const next = { ...prev };
        delete next[assistantNewMessage.id];
        return next;
      });
      setAssistantMessageBlocksById(prev => {
        const next = { ...prev };
        delete next[assistantNewMessage.id];
        return next;
      });
      setStatus(AssistantChatStatus.ERROR);
      const errorDisplayMessage: IChatMessage = {
        id: createUUID(),
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'assistant',
        content: "Sorry, I couldn't process your request at this time. Please try again later."
      };
      setMessages(prev => [...prev, errorDisplayMessage]);
    }
    return true;
  }, [
    effectiveAIMode,
    assistantContext,
    assistantModelSyncFingerprint,
    assistantModelSyncState,
    buildAssistantSyncPayload,
    buildMessagesWithToolSummaries,
    chatService,
    coordinator,
    currentModelAssociation,
    hasPendingToolApprovals,
    messages,
    status,
    streamCompletionResponse,
  ]);

  const handleSubmit = async (nextMessageId: UUID) => {
    await submitUserMessage(input, nextMessageId);
  };

  const submitCannedExchange = useCallback(async (
    userContent: string,
    reply: string,
    nextMessageId: UUID,
  ) => {
    try {
      const userMessage: IChatMessage = {
        id: nextMessageId,
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'user',
        content: userContent,
      };
      setMessages(prev => [...prev, userMessage]);
      setStatus(AssistantChatStatus.SUBMITTED);
      await coordinator.waitForFlowMessageAnimation();

      const assistantMessage: IChatMessage = {
        id: createUUID(),
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'assistant',
        content: '',
      };
      setMessages(prev => [...prev, assistantMessage]);
      streamCompletionResponse(new SimulatedStreamingResponse(reply), assistantMessage.id);
    } catch (error) {
      setStatus(AssistantChatStatus.ERROR);
      console.error(error);
      const errorDisplayMessage: IChatMessage = {
        id: createUUID(),
        createdAt: new Date().toISOString(),
        type: 'text',
        role: 'assistant',
        content: 'Sorry, I encountered an issue. Please try again.',
      };
      setMessages(prev => [...prev, errorDisplayMessage]);
    }
  }, [coordinator, streamCompletionResponse]);

  const handleWelcomeAction = useCallback(async (action: WelcomeAction, actionIndex: number) => {
    if (status !== AssistantChatStatus.IDLE && status !== AssistantChatStatus.ERROR) {
      return;
    }

    if (hasPendingToolApprovals) {
      return;
    }

    if (action.kind === 'prompt') {
      if (action.prompt.trim() === '') {
        return;
      }

      await coordinator.withStagedUserMessage(action.prompt, async nextMessageId => {
        await submitUserMessage(action.prompt, nextMessageId);
      });
      return;
    }

    await coordinator.withStagedFlowMessage(actionIndex, async nextMessageId => {
      await submitCannedExchange(action.label, action.reply, nextMessageId);
    });
  }, [coordinator, hasPendingToolApprovals, status, submitCannedExchange, submitUserMessage]);

  const handleReset = () => {
    setMessages(getInitialMessages());
    setStatus(AssistantChatStatus.IDLE);
    setInput('');
    setSessionId(createUUID());
    setEsetSessionId(undefined);
    setSessionTitle(undefined);
    setIsGeneratingTitle(false);
    setLastTitleAttemptAt(0);
    setLastSyncedModelStateFingerprint(undefined);
    setLastSyncedModelState(null);
    setFeedback({});
    setToolExecutionsByMessage({});
    setAssistantMessageBlocksById({});
    setSessionModelAssociation(null);
    if (sessionStateSaveTimeoutRef.current) {
      clearTimeout(sessionStateSaveTimeoutRef.current);
      sessionStateSaveTimeoutRef.current = null;
    }
    lastSavedSessionStateRef.current = null;
  };

  const handleFeedbackSubmit = async (messageId: UUID, feedbackType: 'positive' | 'negative', userFeedbackText?: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) {
      console.error('Message not found for feedback submission:', messageId);
      return;
    }

    const previousFeedback = feedback[messageId];
    setFeedback(prev => ({
      ...prev,
      [messageId]: {
        type: feedbackType,
        feedbackEntryUUID: previousFeedback?.feedbackEntryUUID,
        isSubmitted: false
      },
    }));

    try {
      const feedbackEntryUUID = await feedbackService.submitFeedback({
        feedbackType,
        sessionId,
        queryText: message.content,
        feedbackEntryUUID: previousFeedback?.feedbackEntryUUID,
        userFeedbackText
      });

      setFeedback(prev => ({
        ...prev,
        [messageId]: {
          type: feedbackType,
          feedbackEntryUUID,
          isSubmitted: true
        },
      }));
    } catch (error) {
      console.error('Failed to submit feedback through hook:', error);
      setFeedback(prev => ({
        ...prev,
        [messageId]: previousFeedback,
      }));
    }
  };

  return {
    coordinator,
    messages,
    input,
    handleInputChange,
    setInputValue,
    handleSubmit,
    handleWelcomeAction,
    status,
    handleReset,
    handleFeedbackSubmit,
    feedback,
    toolExecutionsByMessage,
    assistantMessageBlocksById,
    isOpen,
    toggleAssistant: setIsOpen,
    loadSessionFromHistory,
    isRestoringSession,
    sessionId,
    sessionTitle,
    aiMode: effectiveAIMode,
    setAIMode,
    isModelPage,
    isViewOnlyModel,
    hasModelOutput,
    hasModelChanges,
    modelChangeCount,
    currentModelAssociation,
    sessionModelAssociation,
    handleKeepAllChanges,
    handleUndoAllChanges,
    pendingToolApprovalCount,
    hasPendingToolApprovals,
    approvePendingToolChanges,
    rejectPendingToolChanges,
  };
}

const SYSTEM_MESSAGE_CONTENT = `
By interacting with ESET's AI
Assistant, you agree to the [Security & Privacy Policy](https://www.pnnl.gov/security-privacy). Your information will remain private and will not be shared without your permission.

**Note:** This assistant is a beta feature and may occasionally provide inaccurate information. Please verify responses as needed.
      `;

const createSystemMessage = (): IChatMessage => ({
  id: createUUID(),
  createdAt: new Date().toISOString(),
  type: 'text',
  role: 'system',
  content: SYSTEM_MESSAGE_CONTENT,
});

const createInitialAssistantMessage = (): IChatMessage => ({
  id: createUUID(),
  createdAt: new Date().toISOString(),
  type: 'text',
  role: 'assistant',
  content: WELCOME_MESSAGE_CONTENT,
  isWelcome: true,
});

const getInitialMessages = (): IChatMessage[] => ([
  createSystemMessage(),
  createInitialAssistantMessage()
]);
